/**
 * REST API Adapter
 * Base44 API'sini taklit eden REST API tabanlı veri katmanı
 * Özel backend'ler için kullanılabilir
 *
 * Beklenen API yapısı:
 * GET    /api/{entity}         - List
 * GET    /api/{entity}/:id     - Get
 * POST   /api/{entity}         - Create
 * PUT    /api/{entity}/:id     - Update
 * DELETE /api/{entity}/:id     - Delete
 */

// API config - .env dosyasından alınmalı
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

let authToken = null;

/**
 * Auth token'ı ayarlar
 */
export function setAuthToken(token) {
  authToken = token;
}

/**
 * Auth token'ı temizler
 */
export function clearAuthToken() {
  authToken = null;
}

/**
 * HTTP request helper
 */
async function request(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  // 204 No Content
  if (response.status === 204) {
    return { success: true };
  }

  return response.json();
}

/**
 * Query string oluşturur
 */
function buildQueryString(params) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v));
      } else {
        searchParams.append(key, value);
      }
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Entity adını endpoint'e çevirir
 */
function entityToEndpoint(entityName) {
  const endpointMap = {
    Lead: '/leads',
    Task: '/tasks',
    Product: '/products',
    Order: '/orders',
    CustomerPrice: '/customer-prices',
    ActivityReport: '/activity-reports',
    IncomingOffer: '/incoming-offers',
  };
  return endpointMap[entityName] || `/${entityName.toLowerCase()}s`;
}

/**
 * REST API Entity sınıfı
 */
class RestEntity {
  constructor(name) {
    this.name = name;
    this.endpoint = entityToEndpoint(name);
  }

  /**
   * Tüm kayıtları listeler
   */
  async list(sortField = null) {
    const params = {};

    if (sortField) {
      const desc = sortField.startsWith('-');
      params.sort = desc ? sortField.slice(1) : sortField;
      params.order = desc ? 'desc' : 'asc';
    }

    return request(`${this.endpoint}${buildQueryString(params)}`);
  }

  /**
   * Filtrelenmiş kayıtları döndürür
   */
  async filter(filters = {}, sortField = null) {
    const params = { ...filters };

    if (sortField) {
      const desc = sortField.startsWith('-');
      params.sort = desc ? sortField.slice(1) : sortField;
      params.order = desc ? 'desc' : 'asc';
    }

    return request(`${this.endpoint}${buildQueryString(params)}`);
  }

  /**
   * Tek kayıt getirir
   */
  async get(id) {
    return request(`${this.endpoint}/${id}`);
  }

  /**
   * Yeni kayıt oluşturur
   */
  async create(record) {
    return request(this.endpoint, {
      method: 'POST',
      body: JSON.stringify(record),
    });
  }

  /**
   * Kayıt günceller
   */
  async update(id, updates) {
    return request(`${this.endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Kayıt siler
   */
  async delete(id) {
    return request(`${this.endpoint}/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Toplu kayıt oluşturur
   */
  async createMany(records) {
    return request(`${this.endpoint}/bulk`, {
      method: 'POST',
      body: JSON.stringify({ items: records }),
    });
  }

  /**
   * Kayıt sayısını döndürür
   */
  async count(filters = {}) {
    const params = { ...filters, count: true };
    const result = await request(`${this.endpoint}${buildQueryString(params)}`);
    return result.count || result.length || 0;
  }
}

/**
 * REST API Adapter
 */
export const RestApiAdapter = {
  entities: {
    Lead: new RestEntity('Lead'),
    Task: new RestEntity('Task'),
    Product: new RestEntity('Product'),
    Order: new RestEntity('Order'),
    CustomerPrice: new RestEntity('CustomerPrice'),
    ActivityReport: new RestEntity('ActivityReport'),
    IncomingOffer: new RestEntity('IncomingOffer'),
  },

  /**
   * Auth token ayarlar
   */
  setToken: setAuthToken,

  /**
   * Auth token temizler
   */
  clearToken: clearAuthToken,

  /**
   * Custom request (auth endpoint'leri vb. için)
   */
  request,
};

export default RestApiAdapter;
